import * as React from 'react';
import { Star, Clock, Search, ChevronsUpDown, X, Check, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import type { CostCentre } from '@/types/timesheet';
import { useCostCentreSelector } from '@/hooks/use-cost-centre-selector';
import { CostCentreSearchModal } from './CostCentreSearchModal';

interface CostCentreSelectorProps {
  costCentres: CostCentre[];
  value?: string;
  onChange: (costCentreId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  isSubmitted?: boolean;
  /** When false, the Favourites section is hidden in both the dropdown and the full search modal */
  showFavourites?: boolean;
  // Server-side pagination props (optional)
  onLoadMore?: () => void;
  hasMore?: boolean;
  remainingCount?: number;
  isLoadingMore?: boolean;
  // Server-side search props (optional)
  onServerSearch?: (query: string) => void;
  isSearching?: boolean;
  totalCount?: number;
  fallbackName?: string;
  fallbackCode?: string;
}

export function CostCentreSelector({
  costCentres,
  value,
  onChange,
  disabled,
  placeholder = 'Select cost centre...',
  required = false,
  isSubmitted = false,
  showFavourites = true,
  onLoadMore,
  hasMore,
  remainingCount,
  isLoadingMore,
  onServerSearch,
  isSearching,
  totalCount,
  fallbackName,
  fallbackCode,
}: CostCentreSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const popoverContentRef = React.useRef<HTMLDivElement>(null);

  const {
    enrichedCostCentres,
    favouriteCostCentres,
    recentCostCentres,
    toggleFavourite,
    recordCostCentreAccess,
    isFavourite,
  } = useCostCentreSelector(costCentres);

  const selectedCostCentre = enrichedCostCentres.find((cc) => cc.id === value);

  // Filter cost centres based on search query
  const filteredCostCentres = React.useMemo(() => {
    if (!searchQuery.trim()) return enrichedCostCentres;
    const query = searchQuery.toLowerCase();
    return enrichedCostCentres.filter(
      (cc) =>
        cc.name.toLowerCase().includes(query) ||
        cc.code.toLowerCase().includes(query)
    );
  }, [enrichedCostCentres, searchQuery]);

  const filteredFavourites = React.useMemo(() => {
    if (!searchQuery.trim()) return favouriteCostCentres.slice(0, 5);
    return favouriteCostCentres.filter((cc) =>
      filteredCostCentres.some((fcc) => fcc.id === cc.id)
    );
  }, [favouriteCostCentres, filteredCostCentres, searchQuery]);

  const filteredRecent = React.useMemo(() => {
    if (!searchQuery.trim()) return recentCostCentres.slice(0, 5);
    return recentCostCentres.filter((cc) =>
      filteredCostCentres.some((fcc) => fcc.id === cc.id)
    );
  }, [recentCostCentres, filteredCostCentres, searchQuery]);

  const additionalCostCentres = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const favouriteIds = new Set(filteredFavourites.map((cc) => cc.id));
    const recentIds = new Set(filteredRecent.map((cc) => cc.id));
    return filteredCostCentres
      .filter((cc) => !favouriteIds.has(cc.id) && !recentIds.has(cc.id))
      .slice(0, 10);
  }, [filteredCostCentres, filteredFavourites, filteredRecent, searchQuery]);

  const hasNoResults =
    !!searchQuery &&
    filteredFavourites.length === 0 &&
    filteredRecent.length === 0 &&
    additionalCostCentres.length === 0;

  const hasNoDefault =
    !searchQuery &&
    filteredRecent.length === 0 &&
    (showFavourites ? filteredFavourites.length === 0 : true);

  // Handle cost centre selection
  const handleSelectCostCentre = React.useCallback(
    (costCentreId: string) => {
      onChange(costCentreId);
      recordCostCentreAccess(costCentreId);
      setDropdownOpen(false);
    },
    [onChange, recordCostCentreAccess]
  );

  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
    setDropdownOpen(false);
    setSearchQuery('');
  }, []);

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!dropdownOpen) {
      setSearchQuery('');
    }
  }, [dropdownOpen]);

  const triggerLabel = selectedCostCentre
    ? `${selectedCostCentre.name} (${selectedCostCentre.code})`
    : fallbackName
      ? `${fallbackName}${fallbackCode ? ` (${fallbackCode})` : ''}`
      : '';

  return (
    <>
      <Popover
        open={!disabled && dropdownOpen}
        onOpenChange={(open) => !disabled && setDropdownOpen(open)}
      >
        <PopoverTrigger asChild>
          <div
            role="combobox"
            aria-expanded={dropdownOpen}
            aria-disabled={disabled || undefined}
            aria-label="Select cost centre"
            className={cn(
              'border-input flex h-9 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
              'cursor-pointer hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              disabled && 'cursor-not-allowed opacity-50 pointer-events-none',
              required && !value && !isSubmitted && 'border-destructive focus-visible:ring-destructive'
            )}
            onClick={() => !disabled && setDropdownOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                !disabled && setDropdownOpen(true);
              }
              if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                !disabled && handleOpenModal();
              }
            }}
            tabIndex={disabled ? -1 : 0}
          >
            <span className={cn('flex-1 truncate', !triggerLabel && 'text-muted-foreground')}>
              {triggerLabel || placeholder}
            </span>
            <div className="flex items-center gap-1 shrink-0 ml-1">
              {selectedCostCentre && !disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange('');
                  }}
                  className="text-muted-foreground hover:text-foreground rounded-sm p-0.5"
                  aria-label="Clear selection"
                  tabIndex={-1}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="p-0"
          style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '300px' }}
          align="start"
          ref={popoverContentRef}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => {
              const input = popoverContentRef.current?.querySelector('input');
              input?.focus();
            }, 0);
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            {/* Search input */}
            <CommandInput
              placeholder="Search by code or name..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />

            <CommandList className="max-h-[360px]">
              {/* Empty states */}
              {hasNoResults && (
                <CommandEmpty>
                  <div className="py-6 text-center">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No cost centres found for "{searchQuery}"</p>
                    <p className="text-xs mt-1 text-muted-foreground">Try a different search term</p>
                  </div>
                </CommandEmpty>
              )}
              {hasNoDefault && !hasNoResults && (
                <CommandEmpty>
                  <span className="text-muted-foreground">
                    {showFavourites
                      ? 'No favourites or recent cost centres'
                      : 'No recently used cost centres'}
                  </span>
                </CommandEmpty>
              )}

              {/* Favourites */}
              {showFavourites && filteredFavourites.length > 0 && (
                <CommandGroup
                  heading={
                    <span className="flex items-center gap-1.5">
                      <Star className="h-3.5 w-3.5" />
                      FAVOURITES
                    </span>
                  }
                >
                  {filteredFavourites.map((cc) => (
                    <CommandItem
                      key={cc.id}
                      value={`fav-${cc.id}`}
                      onSelect={() => handleSelectCostCentre(cc.id)}
                      className="gap-2"
                    >
                      <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 shrink-0" />
                      <span className="flex-1 truncate">
                        {cc.name}{' '}
                        <span className="text-muted-foreground">({cc.code})</span>
                      </span>
                      {value === cc.id && <Check className="h-4 w-4 shrink-0" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Recently Used */}
              {filteredRecent.length > 0 && (
                <>
                  {showFavourites && filteredFavourites.length > 0 && <CommandSeparator />}
                  <CommandGroup
                    heading={
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        RECENTLY USED
                      </span>
                    }
                  >
                    {filteredRecent.map((cc) => (
                      <CommandItem
                        key={cc.id}
                        value={`recent-${cc.id}`}
                        onSelect={() => handleSelectCostCentre(cc.id)}
                        className="gap-2"
                      >
                        <span className="flex-1 truncate">
                          {cc.name}{' '}
                          <span className="text-muted-foreground">({cc.code})</span>
                        </span>
                        {value === cc.id && <Check className="h-4 w-4 shrink-0" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {/* Other search results */}
              {searchQuery && additionalCostCentres.length > 0 && (
                <>
                  {(filteredFavourites.length > 0 || filteredRecent.length > 0) && (
                    <CommandSeparator />
                  )}
                  <CommandGroup
                    heading={
                      <span className="flex items-center gap-1.5">
                        <Search className="h-3.5 w-3.5" />
                        OTHER RESULTS
                      </span>
                    }
                  >
                    {additionalCostCentres.map((cc) => (
                      <CommandItem
                        key={cc.id}
                        value={`other-${cc.id}`}
                        onSelect={() => handleSelectCostCentre(cc.id)}
                        className="gap-2"
                      >
                        <span className="flex-1 truncate">
                          {cc.name}{' '}
                          <span className="text-muted-foreground">({cc.code})</span>
                        </span>
                        {value === cc.id && <Check className="h-4 w-4 shrink-0" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>

            {/* Show More footer */}
            <Separator />
            <div className="p-1">
              <Button
                variant="ghost"
                className="w-full justify-center text-sm gap-1.5"
                onClick={handleOpenModal}
              >
                <ArrowRight className="h-4 w-4" />
                Show More...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Full Search Modal */}
      <CostCentreSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        costCentres={enrichedCostCentres}
        value={value}
        onSelect={handleSelectCostCentre}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
        showFavourites={showFavourites}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        remainingCount={remainingCount}
        isLoadingMore={isLoadingMore}
        onServerSearch={onServerSearch}
        isSearching={isSearching}
        totalCount={totalCount}
      />
    </>
  );
}
